import clamp from 'lodash-es/clamp'
import type { AnchorPoint, AnyDomainEntity, EnhancedDomain } from 'metalworks-domain'
import {
  $AnyBoltHole,
  $AnyCountersinkHole,
  $FixingHole,
  AnchorPointPosition,
  AnyBoltHole,
  AnyCountersinkHole,
  CornerFixingHoles,
  Hole,
  KickPlateFixingHoles,
  Model,
  SingleFixingHole,
} from 'metalworks-domain'
import { match } from 'ts-pattern'

import { applyUpdates, createInitialDomainState } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../configurator-interface.js'

const cornerPositions: AnchorPointPosition[] = [
  AnchorPointPosition.topLeft,
  AnchorPointPosition.topRight,
  AnchorPointPosition.bottomLeft,
  AnchorPointPosition.bottomRight,
]

const horizontalEdgePositions: AnchorPointPosition[] = [AnchorPointPosition.top, AnchorPointPosition.bottom]
const verticalEdgePositions: AnchorPointPosition[] = [AnchorPointPosition.left, AnchorPointPosition.right]
const edgePositions = [...horizontalEdgePositions, ...verticalEdgePositions]

/**
 * This transformer automatically adds the anchor points associated with an entity
 * when that entity is created.
 */
export const positionFeatureTransformer = configuratorInterface.createTransformer(ctx => (updates, previousState) => {
  const createFeatureUpdate = updates.filter(u => configuratorInterface.isCreateEntityUpdate(u, Hole.types)).at(0)

  if (!createFeatureUpdate) {
    return updates
  }

  const entity = createFeatureUpdate.entity

  if (!Hole.is(entity)) {
    return updates
  }

  const otherUpdates = updates.filter(u => u !== createFeatureUpdate)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const newState = applyUpdates<AnyDomainEntity>(updates as any, {
    entities: previousState.entities,
    ephemeralRecords: previousState.ephemeralRecords,
    rootEntityId: previousState.rootEntityId,
    rootEntityType: previousState.rootEntityType,
  })

  const domainState = createInitialDomainState(newState.entities, previousState.rootEntityType)

  const $plate = ctx.domain.getRoot$Entity(domainState) as EnhancedDomain.$Plate
  const $bend = $plate.$customBend
  const $plateAnchorPoints = $plate.$anchorPoints

  const featureSettings = $plate.$featureSettings.find(
    fs => fs.plateType === $plate.type && fs.featureType === entity.type
  )

  let $selectedPlateAnchorPoint: AnchorPoint | undefined

  if (CornerFixingHoles.is(entity) || KickPlateFixingHoles.is(entity)) {
    // Corner fixing holes and kick plate fixing holes are always positioned
    // in the bottom left corner. This can't be overridden in admin.
    $selectedPlateAnchorPoint = $plateAnchorPoints.find(ap => ap.position === AnchorPointPosition.bottomLeft)
  } else if (featureSettings) {
    // If entity was configured to have a specific anchor point, use it.
    $selectedPlateAnchorPoint = $plateAnchorPoints.find(ap => ap.position === featureSettings.anchorPointPosition)
  } else if (SingleFixingHole.is(entity)) {
    // If there's no override, position fixing holes to the top left corner by default.
    $selectedPlateAnchorPoint = $plateAnchorPoints.find(ap => ap.position === AnchorPointPosition.topLeft)
  }

  // By default, when there's no override, all other features are positioned in the center.
  if (!$selectedPlateAnchorPoint) {
    $selectedPlateAnchorPoint = $plateAnchorPoints.find(ap => ap.position === AnchorPointPosition.center)
  }

  if (!$selectedPlateAnchorPoint) {
    throw new Error('Anchor point is missing!')
  }

  // If the selected anchor point is already taken by a fixing hole, try to find another available anchor point.
  if (SingleFixingHole.is(entity)) {
    const isAnchorPointTaken = (anchorPoint: AnchorPoint) =>
      newState.entities.some(e => SingleFixingHole.is(e) && e.anchorPointToId === anchorPoint.id)

    if (isAnchorPointTaken($selectedPlateAnchorPoint)) {
      // If feature is configured to be positioned in a corner, try to find another corner anchor point.
      // If it's positioned one of the edges, try to find another edge anchor point.
      const anchorPointPool = edgePositions.includes($selectedPlateAnchorPoint.position)
        ? edgePositions
        : cornerPositions

      const nextAvailableAnchorPoint = $plateAnchorPoints.find(
        ap => anchorPointPool.includes(ap.position) && !isAnchorPointTaken(ap)
      )

      if (nextAvailableAnchorPoint) {
        $selectedPlateAnchorPoint = nextAvailableAnchorPoint
      }
    }
  }

  const $entity = ctx.domain.get$Entity(entity.id, domainState)

  const newEntityAnchorPointId = $entity.$anchorPoints.find(ap => ap.position === AnchorPointPosition.center)?.id
  if (!newEntityAnchorPointId) {
    throw new Error('Newly created center anchor point not found!')
  }

  // For fixing hole entities try to find existing hole to inherit its offset.
  let $existingHole: $FixingHole | undefined
  if (SingleFixingHole.is(entity)) {
    const plateAnchorPointIds = $plateAnchorPoints.map(ap => ap.id)

    $existingHole = $plate.$holes.find(e => $FixingHole.is(e) && plateAnchorPointIds.includes(e.anchorPointToId)) as
      | $FixingHole
      | undefined
  }

  // Existing hole offset has higher priotity than settings configured in admin.
  let relX = $existingHole?.x ?? featureSettings?.offsetX
  let relY = $existingHole?.y ?? featureSettings?.offsetY

  // Copy the size of the existing hole if it exists, otherwise use the size of the feature.
  const sizeX = $existingHole?.$sizeX ?? $entity.$sizeX
  const sizeY = $existingHole?.$sizeY ?? $entity.$sizeY

  const plateSizeX = $plate.$sizeX
  const plateSizeY = $plate.$sizeY

  const centerX = $bend?.$middlePoint.x ?? plateSizeX / 2
  const centerY = $bend?.$middlePoint.y ?? plateSizeY / 2

  // Minimal offset when the feature is not too close to the edge of the plate.
  const minOffsetX = sizeX / 2 + $plate.$minimumDistanceHoleToEdge
  const minOffsetY = sizeY / 2 + $plate.$minimumDistanceHoleToEdge

  const minBendOffsetX = sizeX / 2 + ($bend?.$safeZone ?? 0)
  const minBendOffsetY = sizeY / 2 + ($bend?.$safeZone ?? 0)

  const safeArea = ($bend?.$safeZone ?? 0) + $plate.$minimumDistanceHoleToEdge

  // Maximum offset when the feature is not too close to the edge or center of the plate
  const maxFlatOffsetToHalfX = $plate.$sizeX / 2 - sizeX / 2 - $plate.$minimumDistanceHoleToEdge
  const maxFlatOffsetToHalfY = $plate.$sizeY / 2 - sizeY / 2 - $plate.$minimumDistanceHoleToEdge

  const maxOffsetToHalfX = match($selectedPlateAnchorPoint.position)
    .with(AnchorPointPosition.topLeft, AnchorPointPosition.bottomLeft, AnchorPointPosition.left, () =>
      Math.abs(centerX - safeArea)
    )
    .with(AnchorPointPosition.topRight, AnchorPointPosition.bottomRight, AnchorPointPosition.right, () =>
      Math.abs(-plateSizeX + centerX + safeArea)
    )
    .otherwise(() => 0)

  const maxOffsetToHalfY = match($selectedPlateAnchorPoint.position)
    .with(AnchorPointPosition.topLeft, AnchorPointPosition.topRight, AnchorPointPosition.top, () =>
      Math.abs(-plateSizeY + centerY + safeArea)
    )
    .with(AnchorPointPosition.bottomLeft, AnchorPointPosition.bottomRight, AnchorPointPosition.bottom, () =>
      Math.abs(centerY - safeArea)
    )
    .otherwise(() => 0)

  if (cornerPositions.includes($selectedPlateAnchorPoint.position)) {
    // Position the feature in the same quadrant as the selected anchor point if possible.
    // If plate is too small, `minOffsetY` will push the feature over the center of the plate.
    relX = clamp(Math.abs(relX ?? $entity.$defaultCornerOffset), minOffsetX, maxOffsetToHalfX)
    relY = clamp(Math.abs(relY ?? $entity.$defaultCornerOffset), minOffsetY, maxOffsetToHalfY)
  } else if (horizontalEdgePositions.includes($selectedPlateAnchorPoint.position)) {
    // Position from the top or bottom edge. Do not get too close to the edge of the plate.
    // If plate is too small, `minOffsetY` will push the feature over the center of the plate.
    relX = clamp(Math.abs(relX ?? 0), -maxOffsetToHalfX, maxOffsetToHalfX)
    relY = clamp(Math.abs(relY ?? 0), minOffsetY, maxOffsetToHalfY)
  } else if (verticalEdgePositions.includes($selectedPlateAnchorPoint.position)) {
    // Position from the left or right edge. Do not get too close to the edge of the plate.
    // If plate is too small, `minOffsetX` will push the feature over the center of the plate.
    relX = clamp(Math.abs(relX ?? 0), minOffsetX, maxOffsetToHalfX)
    relY = clamp(Math.abs(relY ?? 0), -maxOffsetToHalfY, maxOffsetToHalfY)
  } else {
    // Position from the center. Do not get too close to the edge of the plate in any direction.
    relX = clamp(relX ?? 0, -maxFlatOffsetToHalfX, maxFlatOffsetToHalfX)
    relY = clamp(relY ?? 0, -maxFlatOffsetToHalfY, maxFlatOffsetToHalfY)
  }

  const absX = match($selectedPlateAnchorPoint.position)
    .with(AnchorPointPosition.topLeft, AnchorPointPosition.bottomLeft, AnchorPointPosition.bottom, () => relX!)
    .with(
      AnchorPointPosition.topRight,
      AnchorPointPosition.bottomRight,
      AnchorPointPosition.top,
      () => plateSizeX - relX!
    )
    .otherwise(() => centerX + relX!)

  const absY = match($selectedPlateAnchorPoint.position)
    .with(AnchorPointPosition.topLeft, AnchorPointPosition.topRight, AnchorPointPosition.top, () => plateSizeY - relY!)
    .with(AnchorPointPosition.bottomLeft, AnchorPointPosition.bottomRight, AnchorPointPosition.bottom, () => relY!)
    .otherwise(() => centerY + relY!)

  if ($bend) {
    if ($bend.orientation === Model.BendOrientation.Horizontal) {
      const isTooCloseToBend = Math.abs(absY - $bend.$middlePoint.y) < minBendOffsetY

      if (isTooCloseToBend) {
        const bottomSize = $bend.startInnerLength
        const topSize = $bend.endInnerLength

        relY =
          bottomSize >= topSize
            ? clamp(-(bottomSize / 2), -minBendOffsetY, -bottomSize + minOffsetY)
            : clamp(topSize / 2, minBendOffsetY, topSize - minOffsetY)
      }
    } else {
      const isTooCloseToBend = Math.abs(absX - $bend.$middlePoint.x) < minBendOffsetX

      if (isTooCloseToBend) {
        const leftSize = $bend.startInnerLength
        const rightSize = $bend.endInnerLength

        relX =
          leftSize >= rightSize
            ? clamp(-(leftSize / 2), -leftSize + minOffsetX, -minBendOffsetX)
            : clamp(rightSize / 2, minBendOffsetX, rightSize - minOffsetX)
      }
    }
  }

  // Fix the sign of the X/Y offsets based on the selected anchor point.
  const coords = match($selectedPlateAnchorPoint.position)
    .with(AnchorPointPosition.topLeft, () => ({ x: Math.abs(relX), y: -Math.abs(relY) }))
    .with(AnchorPointPosition.topRight, () => ({ x: -Math.abs(relX), y: -Math.abs(relY) }))
    .with(AnchorPointPosition.bottomLeft, () => ({ x: Math.abs(relX), y: Math.abs(relY) }))
    .with(AnchorPointPosition.bottomRight, () => ({ x: -Math.abs(relX), y: Math.abs(relY) }))
    .with(AnchorPointPosition.top, () => ({ x: relX, y: -Math.abs(relY) }))
    .with(AnchorPointPosition.bottom, () => ({ x: relX, y: Math.abs(relY) }))
    .with(AnchorPointPosition.left, () => ({ x: Math.abs(relX), y: relY }))
    .with(AnchorPointPosition.right, () => ({ x: -Math.abs(relX), y: relY }))
    .otherwise(() => ({ x: relX, y: relY }))

  // TODO: can all of these be expressions now?
  const createHoleUpdate = configuratorInterface.updates.DomainEntityCreation({
    entity: {
      ...createFeatureUpdate.entity,
      anchorPointFromId: newEntityAnchorPointId,
      anchorPointToId: $selectedPlateAnchorPoint.id,
      // TODO: Default values for x and y here should be specified in the admin panel, and the actual value
      // used also needs to take into account the hole size and plate size so that they are valid.
      ...coords,
      ...(($existingHole && $AnyBoltHole.is($existingHole) && AnyBoltHole.is(createFeatureUpdate.entity)
        ? { radius: $existingHole.radius }
        : {}) as any), // TODO: fix types
      ...(($existingHole && $AnyCountersinkHole.is($existingHole) && AnyCountersinkHole.is(createFeatureUpdate.entity)
        ? {
            bottomRadius: $existingHole.bottomRadius,
            topRadius: $existingHole.topRadius,
            countersinkId: $existingHole.countersinkId,
          }
        : {}) as any), // TODO: fix types
    },
    updateTag: createFeatureUpdate.updateTag,
    // TODO: Is this correct?
    parentId: entity.id,
  })

  return [createHoleUpdate, ...otherUpdates]
})
